import type { Dayjs } from "dayjs";

import { ChevronLeft, ChevronRight } from "lucide-react";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Button } from "@/components/button";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";

import { BrandFilter } from "../BrandFilter";

export type CalendarView = "month" | "week";

const VIEW_OPTIONS = [
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
];

export function CalendarToolbar({
  brandIds,
  date,
  onBrandChange,
  onNavigate,
  onToday,
  onViewChange,
  view,
}: {
  brandIds: number[];
  date: Dayjs;
  onBrandChange: (brandIds: number[]) => void;
  onNavigate: (direction: -1 | 1) => void;
  onToday: () => void;
  onViewChange: (view: CalendarView) => void;
  view: CalendarView;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-md">
      <BrandFilter brandIds={brandIds} onChange={onBrandChange} />
      <Button color="gray" onPress={onToday} variant="secondary">
        Today
      </Button>

      <div className="flex items-center gap-xs">
        <Button
          aria-label="Previous"
          color="gray"
          isIconOnly
          onPress={() => onNavigate(-1)}
          variant="unstyled"
        >
          <AccessibleIcon>
            <ChevronLeft className="text-fg-quaternary" />
          </AccessibleIcon>
        </Button>
        <span className="min-w-[120px] text-center text-sm font-semibold text-text-primary">
          {date.format("MMMM, YYYY")}
        </span>
        <Button
          aria-label="Next"
          color="gray"
          isIconOnly
          onPress={() => onNavigate(1)}
          variant="unstyled"
        >
          <AccessibleIcon>
            <ChevronRight className="text-fg-quaternary" />
          </AccessibleIcon>
        </Button>
      </div>

      <Select
        aria-label="Calendar view"
        className="w-[120px]"
        onSelectionChange={(key) => onViewChange(key as CalendarView)}
        selectedKey={view}
      >
        <SelectButton />
        <SelectPopover>
          <SelectListBox items={VIEW_OPTIONS}>
            {(option) => (
              <SelectListItem id={option.id}>{option.label}</SelectListItem>
            )}
          </SelectListBox>
        </SelectPopover>
      </Select>
    </div>
  );
}
